import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import DemoBanner from '../components/DemoBanner';

interface Submission {
  id: number;
  title: string;
  description?: string;
  link: string;
  created_at: string;
  email?: string;
  user_email?: string;
  user_name?: string;
}

const AllSubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAllSubmissions();
  }, []);

  const fetchAllSubmissions = async () => {
    try {
      setLoading(true);
      const result = await api.getAllEntries();
      if (result.entries) {
        setSubmissions(result.entries);
      }
    } catch (err) {
      setError('Failed to load submissions');
      console.error('Error fetching all submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <DemoBanner />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-xs font-mono text-neutral-500">loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <DemoBanner />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-xs font-mono text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <DemoBanner />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="mb-12">
          <h1 className="text-2xl font-mono font-light text-neutral-900">
            all submissions
          </h1>
          <p className="text-sm font-mono text-neutral-600 mt-2">
            {submissions.length} total submissions
          </p>
          <Link 
            to="/" 
            className="text-xs font-mono text-neutral-500 underline hover:text-neutral-700 mt-2 inline-block"
          >
            ← back to home
          </Link>
        </header>

        <main>
          <div className="border border-neutral-300 p-6">
            {submissions.length === 0 ? (
              <div className="text-xs font-mono text-neutral-500">
                no submissions yet
              </div>
            ) : (
              <div className="space-y-3">
                {submissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="border-b border-neutral-200 pb-3 last:border-0"
                  >
                    <div className="font-mono text-sm text-neutral-900">
                      {submission.title}
                    </div>
                    {submission.description && (
                      <p className="text-xs font-mono text-neutral-500 mt-1">
                        {submission.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      <a
                        href={submission.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-mono text-neutral-700 underline"
                      >
                        view →
                      </a>
                      <span className="text-xs font-mono text-neutral-400">
                        by {submission.user_name || submission.email || submission.user_email || 'unknown'}
                      </span>
                      <span className="text-xs font-mono text-neutral-400">
                        {new Date(submission.created_at).toLocaleDateString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AllSubmissions;
